"use client"

import { useState, useEffect, useCallback } from "react"
import { Helmet } from "react-helmet-async"
import { getProviders } from "../../services/providersApi"
import { getCategory, incrementCategoryViews } from "../../services/categoriesApi"
import { getActivePartnerships } from "../../services/partnershipsApi"
import { getPhoto } from "../../storageService"
import { Link, useNavigate, useParams, useLocation as useLocationHook } from "react-router-dom"
import "./ProvidersList.css"
import "./UrgencySubcategory.css" // Importamos los estilos para la subcategoría DE URGENCIA
import Breadcrumb from "../Breadcrumbs/Breadcrumbs"
import { translateToEnglish } from "../../utils/translations"
import Spinner from "../Spinner/Spinner"
import { User2, Star, ChevronDown, Users, MessageSquare, Home } from "lucide-react"
import { useLocations } from "../../utils/LocationContext"
import Location from "../Location/Location" // Importamos el componente Location
import ProviderImage from "./ProviderImage" // Importamos el componente de imagen asincrónico
import { toast } from "react-toastify" // Importamos toast para las notificaciones
import {
    getUniqueDisplaySubcategories
} from "../../utils/migrationHelpers" // 🎯 Helpers de migración
import PostsList from "../Forum/PostsList"
import NewBadge from "../NewBadge/NewBadge"
import "./PartnerCard.css"

// Helper: detecta si un provider es partner. Jackson serializa el field `isPartner`
// como `partner` (sin el `is`), pero algunos endpoints pueden devolverlo como `isPartner`.
const isPartner = (p) => Boolean(p?.partner ?? p?.isPartner);

// Sort estable: los partners van al tope, manteniendo el orden del resto.
// Solo se aplica a categorías de productos (los partners son de productos).
const sortPartnersFirst = (list, categoryType) => {
    if (categoryType !== "productos") return list;
    const partners = [];
    const others = [];
    for (const p of list) (isPartner(p) ? partners : others).push(p);
    return [...partners, ...others];
};

const ProvidersList = () => {

    const { categoryName, categoryType } = useParams()
    const categoryTypeEng = translateToEnglish(categoryType)
    const locationHook = useLocationHook()
    const { state } = locationHook
    const initialSubcategory = state?.subcategory?.toString().trim() || ""

    // Leer la pestaña inicial desde el query param ?view=posts|providers
    // Posts solo aplica para servicios — productos siempre arrancan en 'providers'.
    const initialView = (() => {
        if (categoryType !== 'servicios') return 'providers'
        const params = new URLSearchParams(locationHook.search)
        const v = params.get('view')
        return v === 'posts' ? 'posts' : 'providers'
    })()

    // Usar el estado global de ubicación del contexto en lugar del localStorage directamente
    const { selectedGlobalLocation } = useLocations()

    // Estado para la ubicación seleccionada inicializado con el valor global
    const [selectedLocation, setSelectedLocation] = useState(selectedGlobalLocation)

    // Actualizar el estado local cuando cambie el global
    useEffect(() => {
        if (selectedGlobalLocation) {
            setSelectedLocation(selectedGlobalLocation)
        }
    }, [selectedGlobalLocation])

    const [providers, setProviders] = useState([])
    const [loading, setLoading] = useState(true)
    const [filters, setFilters] = useState({
        subcategory: initialSubcategory,
        categoryName: categoryName,
        hasCertificates: false,
    })

    // Estado separado para controlar la subcategoría seleccionada
    const [selectedSubcategory, setSelectedSubcategory] = useState(initialSubcategory || "")

    const [subcategories, setSubcategories] = useState([])
    const [subcategoriesOpen, setSubcategoriesOpen] = useState(false)
    const navigate = useNavigate()
    const [lastDocumentId, setLastDocumentId] = useState(null)
    const [hasMore, setHasMore] = useState(true)
    const [pageSize] = useState(Number(process.env.REACT_APP_DEFAULT_PAGE_SIZE) || 12);
    const [noResults, setNoResults] = useState(false)
    const [filtersApplied, setFiltersApplied] = useState(!!initialSubcategory)
    const [error, setError] = useState(null)
    const [activeView, setActiveView] = useState(initialView) // 'providers' or 'posts'

    // Mantener la URL sincronizada con la pestaña activa para que el redirect post-login
    // (ProtectedRoute guarda pathname+search en `from`) vuelva a la pestaña correcta.
    useEffect(() => {
        const params = new URLSearchParams(locationHook.search)
        const current = params.get('view')
        if (activeView === 'posts' && current !== 'posts') {
            params.set('view', 'posts')
            navigate(`${locationHook.pathname}?${params.toString()}`, { replace: true, state: locationHook.state })
        } else if (activeView === 'providers' && current === 'posts') {
            params.delete('view')
            const qs = params.toString()
            navigate(`${locationHook.pathname}${qs ? `?${qs}` : ''}`, { replace: true, state: locationHook.state })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeView])
    const [categoryData, setCategoryData] = useState(null)


    // Función para manejar el cambio de ubicación desde el componente Location
    const handleLocationChange = useCallback((locationData) => {
        setSelectedLocation(locationData)
        // Resetear proveedores y paginación cuando cambie la ubicación
        setProviders([])
        setLastDocumentId(null)
        setHasMore(true)
    }, [])

    const fetchProviders = useCallback(
        async (isLoadMore = false) => {
            setLoading(true)
            setError(null)
            // Solo resetear noResults cuando no es carga adicional
            if (!isLoadMore) {
                setNoResults(false)
            }

            try {
                const filtersForQuery = { ...filters }
                // Usar la ubicación seleccionada o "TODAS" como fallback
                filtersForQuery.location = selectedLocation?.name || "TODAS"

                const currentLastDoc = isLoadMore ? lastDocumentId : null

                const [providersData, categoryData] = await Promise.all([
                    getProviders(filtersForQuery, categoryTypeEng, pageSize, currentLastDoc),
                    getCategory(categoryTypeEng, categoryName),
                ])

                const { providers: fetchedProviders, lastDocumentId: newLastDocumentId } = providersData

                // Si es una carga adicional (isLoadMore) y no se recibieron resultados
                if (isLoadMore && fetchedProviders.length === 0) {
                    // Mostrar toast informando que no hay más resultados
                    toast.info('Has llegado al final. Estos son todos los proveedores disponibles.');
                    // Ocultar botón de cargar más
                    setHasMore(false);
                    setLoading(false);
                    return; // Salir temprano para mantener los proveedores existentes
                }

                setProviders((prev) => {
                    const merged = isLoadMore ? [...prev, ...fetchedProviders] : fetchedProviders;
                    return sortPartnersFirst(merged, categoryType);
                })
                setLastDocumentId(newLastDocumentId)
                setHasMore(fetchedProviders.length === pageSize)

                // Solo establecer noResults si no es carga adicional
                if (!isLoadMore) {
                    setNoResults(fetchedProviders.length === 0)
                }

                if (categoryData?.subcategories) {
                    setSubcategories(categoryData.subcategories)
                    setCategoryData(categoryData) // Store complete category data
                }

                // Guardar el ID de la categoría para el tracking (solo la primera vez)
                if (!isLoadMore && categoryData?.id) {
                    setCategoryId(categoryData.id)
                }
            } catch (error) {
                console.error("Error fetching providers or subcategories:", error)
                if (!isLoadMore) {
                    setNoResults(true)
                }
                setError(error.message || "Error al cargar los proveedores")
            } finally {
                setLoading(false)
            }
        },
        [
            filters,
            categoryTypeEng,
            pageSize,
            lastDocumentId,
            categoryName,
            selectedLocation,
        ]
    )

    useEffect(() => {
        fetchProviders(false)
    }, [categoryName, categoryType, initialSubcategory, filtersApplied, selectedLocation])

    // Estado para almacenar el ID de la categoría
    const [categoryId, setCategoryId] = useState(null)

    // Sponsor del listado: partnership activo que sponsorea esta categoría (solo servicios)
    // + URL del banner resuelto desde Firebase Storage.
    const [sponsor, setSponsor] = useState(null) // { partnership, bannerUrl } | null

    // useEffect para registrar vista de categoría cuando se obtiene el ID
    useEffect(() => {
        // Solo registrar si tenemos categoryId
        if (categoryId) {
            // Registrar la vista de forma asíncrona sin bloquear la carga
            incrementCategoryViews(categoryId).catch(error => {
                // Solo loguear el error sin afectar el flujo del usuario
                console.warn('Error al registrar vista de categoría:', error);
            });
        }
    }, [categoryId]) // Solo cuando cambie categoryId

    // Buscar partnership que sponsoree esta categoría de servicios.
    // El sponsoreo solo aplica a categorías de servicios (los partners son de productos).
    // Usamos getActivePartnerships() + filtro client-side (mismo patrón que probó funcionar
    // en Categories) en vez del endpoint /sponsor/service-category/{id}.
    useEffect(() => {
        if (categoryType !== "servicios" || !categoryId) {
            setSponsor(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const all = await getActivePartnerships();
                if (cancelled || !Array.isArray(all)) {
                    if (!cancelled) setSponsor(null);
                    return;
                }
                const p = all.find(
                    (x) => Array.isArray(x.sponsoredServiceCategories)
                        && x.sponsoredServiceCategories.includes(categoryId)
                );
                if (!p) {
                    if (!cancelled) setSponsor(null);
                    return;
                }
                let bannerUrl = null;
                if (p.bannerPhoto) {
                    try { bannerUrl = await getPhoto(p.bannerPhoto); }
                    catch { bannerUrl = null; }
                }
                if (!cancelled) setSponsor({ partnership: p, bannerUrl });
            } catch (err) {
                console.warn("No se pudo cargar sponsor de la categoría:", err);
                if (!cancelled) setSponsor(null);
            }
        })();
        return () => { cancelled = true; };
    }, [categoryId, categoryType])

    const handleResetFilters = () => {
        setFilters({
            subcategory: "",
            categoryName: categoryName,
            hasCertificates: false,
        })
        setSelectedSubcategory("")
        setLastDocumentId(null)
        setProviders([])
        setFiltersApplied(false)
        setTimeout(() => {
            fetchProviders(false)
        }, 100)
    }

    const handleProviderClick = (providerId) => {
        navigate(`/${providerId}`, { state: { categoryTypeEng, categoryName } })
    }

    const handleSignUpClick = () => {
        navigate(`/registro/proveedor`, { state: { categoryType } })
    }

    const handleLoadMore = () => {
        fetchProviders(true)
    }

    const handleProviderTabClick = () => {
        setActiveView('proveedor')
    }

    const handleSubcategoryChange = (subcategory) => {
        setSelectedSubcategory(subcategory)

        // Asegurar que se actualice correctamente el filtro
        const newFilters = {
            ...filters,
            subcategory: subcategory === "" ? "" : subcategory
        }
        setFilters(newFilters)

        // Auto-aplicar filtro cuando se selecciona una subcategoría
        setLastDocumentId(null)
        setProviders([])
        setFiltersApplied(subcategory !== "")

        // Usar el filtro actualizado directamente para evitar problemas de estado
        setTimeout(() => {
            fetchProvidersWithFilters(newFilters, false)
        }, 100)
    }

    // Nueva función para llamar directamente con filtros específicos
    const fetchProvidersWithFilters = useCallback(
        async (customFilters, isLoadMore = false) => {
            setLoading(true)
            setError(null)
            // No reseteamos setNoResults aquí para mantener los resultados existentes

            try {
                const filtersForQuery = { ...customFilters }
                // Usar la ubicación seleccionada o "TODAS" como fallback
                filtersForQuery.location = selectedLocation?.name || "TODAS"

                const currentLastDoc = isLoadMore ? lastDocumentId : null

                const [providersData, categoryData] = await Promise.all([
                    getProviders(filtersForQuery, categoryTypeEng, pageSize, currentLastDoc),
                    getCategory(categoryTypeEng, categoryName),
                ])

                const { providers: fetchedProviders, lastDocumentId: newLastDocumentId } = providersData


                // Si es una carga adicional (isLoadMore) y no se recibieron resultados
                if (isLoadMore && fetchedProviders.length === 0) {
                    // Mostrar toast informando que no hay más resultados
                    toast.info('Has llegado al final. Estos son todos los proveedores disponibles.');
                    // Ocultar botón de cargar más
                    setHasMore(false);
                    setLoading(false);
                    return; // Salir temprano para mantener los proveedores existentes
                }

                setProviders((prev) => {
                    const merged = isLoadMore ? [...prev, ...fetchedProviders] : fetchedProviders;
                    return sortPartnersFirst(merged, categoryType);
                })
                setLastDocumentId(newLastDocumentId)
                setHasMore(fetchedProviders.length === pageSize)

                // Solo establecemos noResults si no es una carga adicional
                if (!isLoadMore) {
                    setNoResults(fetchedProviders.length === 0)
                }

                if (categoryData?.subcategories) {
                    setSubcategories(categoryData.subcategories)
                }

                // Guardar el ID de la categoría para el tracking (solo la primera vez)
                if (!isLoadMore && categoryData?.id) {
                    setCategoryId(categoryData.id)
                }
            } catch (error) {
                console.error("Error fetching providers or subcategories:", error)
                setNoResults(true)
                setError(error.message || "Error al cargar los proveedores")
            } finally {
                setLoading(false)
            }
        },
        [
            categoryTypeEng,
            pageSize,
            lastDocumentId,
            categoryName,
            selectedLocation,
        ]
    )

    const breadcrumbItems = [
        { label: "Inicio", icon: <Home size={16} />, link: "/" },
        { label: categoryType, link: `/categorias/${categoryType}` },
        { label: categoryName },
    ]

    // Meta tags dinámicos
    const locationText = selectedLocation?.name || 'Argentina';
    const subcategoryText = selectedSubcategory ? ` - ${selectedSubcategory}` : '';
    const pageTitle = `${categoryName}${subcategoryText} en ${locationText} | ContactaMe`;
    const pageDescription = `Encontrá los mejores profesionales de ${categoryName}${subcategoryText} en ${locationText}. ${providers.length} proveedores disponibles. Compará precios y recomendaciones.`;
    const pageUrl = `https://contactame.com.ar/${categoryType}/${categoryName}/proveedores`;

    return (
        <div>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta
                    name="keywords"
                    content={`${categoryName}, ${locationText}, ${categoryType}, profesionales, servicios, contactame${subcategoryText ? ', ' + selectedSubcategory : ''}`}
                />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:image" content="https://contactame.com.ar/logo512.png" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:url" content={pageUrl} />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <meta name="twitter:image" content="https://contactame.com.ar/logo512.png" />

                {/* Canonical URL */}
                <link rel="canonical" href={pageUrl} />
            </Helmet>

            {loading && <Spinner />}

            {/* Contenedor sticky para Location y Breadcrumb */}
            <div className="sticky-header">
                <Location onLocationChange={handleLocationChange} initialLocation={selectedLocation?.name || ""} />
                <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="providers-container">
                <h2 className="page-title">{categoryName.toUpperCase()}</h2>

                {/* "Firma" del partner que sponsorea la categoría: pequeña, casi pegada
                    al título — solo "por" + foto. Link al perfil del partner. */}
                {sponsor && sponsor.bannerUrl && (
                    <Link
                        to={`/${sponsor.partnership.productProviderId}`}
                        className="category-sponsor-sig"
                        title={`por ${sponsor.partnership.partnerName || sponsor.partnership.productProviderId}`}
                    >
                        <span className="category-sponsor-sig__text">por</span>
                        <img
                            src={sponsor.bannerUrl}
                            alt={sponsor.partnership.partnerName || sponsor.partnership.productProviderId}
                            className="category-sponsor-sig__img"
                            loading="lazy"
                        />
                    </Link>
                )}

                {/* Toggle Proveedores/Posts — solo se muestra en servicios */}
                {categoryType === 'servicios' && (
                    <div className="view-toggle" data-active={activeView}>
                        <button
                            className={`toggle-btn ${activeView === 'providers' ? 'active' : ''}`}
                            onClick={() => setActiveView('providers')}
                            aria-pressed={activeView === 'providers'}
                        >
                            <Users size={16} />
                            <span>Proveedores</span>
                        </button>
                        <button
                            className={`toggle-btn ${activeView === 'posts' ? 'active' : ''}`}
                            onClick={() => setActiveView('posts')}
                            aria-pressed={activeView === 'posts'}
                        >
                            <MessageSquare size={16} />
                            <span>Posts</span>
                        </button>
                        <NewBadge size="sm" className="view-toggle__new-badge" />
                    </div>
                )}

                <hr/>

                {activeView === 'posts' ? (
                    <PostsList
                        categoryId={categoryData?.id}
                        categoryName={categoryName}
                    />
                ) : (
                    <>
                        {/* Filtros de subcategorías con chips (colapsables) */}
                        {subcategories.length > 0 && (
                    <div className={`subcategory-filters ${categoryType === "servicios" ? "service-provider" : "product-provider"} ${subcategoriesOpen ? 'open' : ''}`}>
                        <button
                            type="button"
                            className={`subcategory-filters-toggle ${subcategoriesOpen ? 'open' : ''}`}
                            onClick={() => setSubcategoriesOpen(prev => !prev)}
                            aria-expanded={subcategoriesOpen}
                            aria-controls="subcategory-chips-panel"
                        >
                            <span className="subcategory-filters-toggle-label">
                                Filtrar por subcategoría
                                {selectedSubcategory && (
                                    <span className="subcategory-filters-toggle-selected"> · {selectedSubcategory}</span>
                                )}
                            </span>
                            <ChevronDown size={18} className="subcategory-filters-toggle-icon" />
                        </button>
                        {subcategoriesOpen && (
                            <div id="subcategory-chips-panel" className="subcategory-chips-container">
                                <div
                                    className={`subcategory-filter-chip ${selectedSubcategory === "" ? 'active' : ''}`}
                                    onClick={() => handleSubcategoryChange("")}
                                >
                                    TODAS LAS SUBCATEGORÍAS
                                </div>
                                {subcategories.map((sub) => (
                                    <div
                                        key={sub}
                                        className={`subcategory-filter-chip ${sub === "DE URGENCIA" ? 'urgency' : ''} ${selectedSubcategory === sub ? 'active' : ''}`}
                                        onClick={() => handleSubcategoryChange(sub)}
                                    >
                                        {sub}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {error && (
                    <div className="error-message">
                        <p>Ocurrió un error inesperado. No se pudieron obtener los proveedores.</p>
                        <button className="filter-btn btn-outline-gray" onClick={handleResetFilters}>
                            Reintentar
                        </button>
                    </div>
                )}

                {noResults && !error ? (
                    <div className="no-results">
                        <h3>No se encontraron proveedores con los filtros seleccionados</h3>
                        <div
                            className={`provider-card3 signup-card ${
                                categoryType === "servicios" ? "service-provider" : "product-provider"
                            }`}
                            onClick={handleSignUpClick}
                        >
                            <div className="provider-card-content">
                                <div className="provider-header2">
                                    <div className="provider-image-container">
                                        <User2 size={40}/>
                                    </div>
                                    <div className="provider-header-info2">
                                        <h3 className="provider-name">
                                            ¿Brindás {categoryType === "servicios" ? "servicios" : "productos"} de {categoryName}?
                                        </h3>
                                    </div>
                                </div>
                                <p className="provider-description">
                                    Registrate como proveedor y comenzá a ofrecer tus{" "}
                                    {categoryType === "servicios" ? "servicios" : "productos"}.
                                </p>
                                <button
                                    className={`signup-button ${
                                        categoryType === "servicios" ? "service" : "products"
                                    }`}
                                >
                                    Registrate
                                </button>
                            </div>
                        </div>
                    </div>

                ) : (
                    <div className="providers-grid">
                        {providers.map((provider) => {
                            const partner = isPartner(provider);
                            return (
                            <div
                                className={`provider-card3 ${
                                    categoryType === "servicios" ? "service-provider" : "product-provider"
                                } ${partner ? "provider-card3--partner" : ""}`}
                                key={provider.username}
                                onClick={() => handleProviderClick(provider.username)}
                            >
                                {partner && <NewBadge label="PARTNER" size="sm" className="partner-badge" />}
                                <div className="provider-card-content">
                                    <div className="provider-header2">
                                        <div className="provider-image-container">
                                            <ProviderImage
                                                photoPath={provider.photo}
                                                alt={provider.name}
                                                className="provider-image"
                                            />
                                        </div>
                                        <div className="provider-header-info2">
                                            <h3 className="provider-name">
                                                {provider.name.length > 40 ? provider.name.substring(0, 40) + '...' : provider.name}
                                            </h3>
                                            <div className="provider-username">
                                                @{provider.username}
                                                {provider.averageRating > 0 && (
                                                    <span className="rating-chip">
                                                        {Number(provider.averageRating).toFixed(1)}
                                                        <Star size={12} fill="gold" color="gold" />
                                                        <span className="rating-count">({provider.totalReviews || 0})</span>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <p className="provider-description">
                                        {provider.description || "Sin descripción"}
                                    </p>
                                    <div className="provider-chips">
                                        <div className="provider-subcategories">
                                            {(() => {
                                                // 🎯 Usar helper de migración para subcategorías únicas
                                                const uniqueSubs = getUniqueDisplaySubcategories(
                                                    provider.subcategories || [],
                                                    subcategories
                                                );

                                                // Renderizar subcategorías limpias
                                                return uniqueSubs.map(({ original, cleaned }) => (
                                                    <span
                                                        key={original}
                                                        className={`subcategory-chip ${cleaned === "DE URGENCIA" ? 'urgency' : ''}`}
                                                    >
                                                        {cleaned}
                                                    </span>
                                                ));
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                        <div
                            className={`provider-card3 signup-card ${
                                categoryType === "servicios" ? "service-provider" : "product-provider"
                            }`}
                            onClick={handleSignUpClick}
                        >
                            <div className="provider-card-content">
                                <div className="provider-header2">
                                    <div className="provider-image-container">
                                        <User2 size={40}/>
                                    </div>
                                    <div className="provider-header-info2">
                                        <h3 className="provider-name">
                                            ¿Brindás {categoryType === "servicios" ? "servicios" : "productos"} de {categoryName}?
                                        </h3>
                                    </div>
                                </div>
                                <p className="provider-description">
                                    Registrate como proveedor y comenzá a ofrecer tus{" "}
                                    {categoryType === "servicios" ? "servicios" : "productos"}.
                                </p>
                                <button
                                    className={`signup-button ${
                                        categoryType === "servicios" ? "service" : "products"
                                    }`}
                                >
                                    Registrate
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {hasMore && providers.length > 0 && (
                    <div className="load-more-container">
                        <button
                            className={`load-more-button ${
                                categoryType === "servicios" ? "service" : "products"
                            }`}
                            onClick={handleLoadMore}
                            disabled={loading}
                        >
                            {loading ? "Cargando..." : "Cargar más"}
                        </button>
                    </div>
                )}
                    </>
                )}
            </div>
        </div>
    )
}

export default ProvidersList